import * as React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Avatar,
  Button,
  IconButton,
  Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AppNotification } from './types';
import { format } from 'date-fns';
import Linkify from 'linkify-react';
import { IntermediateRepresentation } from 'linkifyjs';

interface NotificationItemProps {
  notification: AppNotification;
  onMarkAsRead: (id: AppNotification['id']) => void;
}

export const linkifyOptions = {
  render: (ir: IntermediateRepresentation): React.ReactElement => {
    return React.createElement(Link, ir.attributes, ir.content);
  }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkAsRead }) => {
  const { id, icon, date, summary, details, actions, isRead } = notification;

  const handleActionClick = (e: React.MouseEvent, onClick: () => void) => {
    e.stopPropagation();
    onClick();
  };

  const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded && !isRead) {
      onMarkAsRead(id);
    }
  };

  return (
    <Accordion
      sx={{
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: isRead ? 'transparent' : 'action.hover',
        '&:before': {
          display: 'none'
        },
        '&.Mui-expanded': {
          margin: 0
        }
      }}
      onChange={handleAccordionChange}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-${id}-content`}
        id={`panel-${id}-header`}
        sx={{
          py: 1.5,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            margin: 0
          }
        }}
      >
        <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: 'secondary.main' }}>{icon}</Avatar>
        <Box flexGrow={1}>
          <Typography variant="body1" sx={{ fontWeight: isRead ? 'normal' : 'bold' }}>
            {summary}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
          {format(date, 'M月d日')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
          <Linkify options={linkifyOptions}>{details}</Linkify>
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          {actions?.map((action, index) => (
            <Button key={index} variant="text" size="small" onClick={(e) => handleActionClick(e, action.onClick)}>
              {action.label}
            </Button>
          ))}
          <Box flexGrow={1} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
